/**
 * Inline list of the user's watchlists for the AddToWatchlistButton popover.
 * Renders the rows + the "new watchlist" inline form. Kept presentational —
 * all state and async ops are owned by AddToWatchlistButton.
 */
import { Check, Loader2, Plus } from "lucide-react";

export default function AddToWatchlistList({
  symbol,
  watchlists,
  addingId,
  onAdd,
  creatingNew,
  setCreatingNew,
  newName,
  setNewName,
  creating,
  onCreate,
}) {
  return (
    <>
      {watchlists.length === 0 && !creatingNew && (
        <div className="px-3 py-4 text-xs text-zinc-500">
          You don&apos;t have any watchlists yet.
        </div>
      )}

      {watchlists.length > 0 && (
        <div className="max-h-64 overflow-auto">
          {watchlists.map((wl) => {
            const already = wl.tickers?.includes(symbol);
            const isAdding = addingId === wl.watchlist_id;
            const count = wl.tickers?.length || 0;
            return (
              <button
                key={wl.watchlist_id}
                data-testid={`add-to-wl-${wl.watchlist_id}`}
                onClick={() => onAdd(wl)}
                disabled={already || isAdding}
                className="w-full text-left px-3 py-2 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/60 transition-colors flex items-center justify-between gap-3 disabled:cursor-not-allowed normal-case tracking-normal"
              >
                <span className="min-w-0">
                  <span className="block text-sm text-zinc-100 truncate">{wl.name}</span>
                  <span className="block text-[10px] font-mono text-zinc-500">
                    {count} ticker{count === 1 ? "" : "s"}
                  </span>
                </span>
                {isAdding ? (
                  <Loader2 size={12} className="animate-spin text-zinc-400" />
                ) : already ? (
                  <Check size={12} className="text-[#00E676]" />
                ) : (
                  <Plus size={12} className="text-zinc-500" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="border-t border-zinc-900">
        {creatingNew ? (
          <div className="p-2 flex items-center gap-2">
            <input
              autoFocus
              data-testid="new-watchlist-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreate();
                if (e.key === "Escape") {
                  setCreatingNew(false);
                  setNewName("");
                }
              }}
              placeholder="Name"
              className="flex-1 bg-[#09090B] border border-zinc-800 focus:border-zinc-500 px-2 h-7 text-xs font-mono outline-none normal-case tracking-normal"
            />
            <button
              data-testid="create-and-add-btn"
              onClick={onCreate}
              disabled={creating || !newName.trim()}
              className="bg-white text-black hover:bg-zinc-200 px-2 h-7 text-[10px] font-mono uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              {creating ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
              Add
            </button>
          </div>
        ) : (
          <button
            data-testid="open-new-watchlist-form"
            onClick={() => setCreatingNew(true)}
            className="w-full text-left px-3 py-2 inline-flex items-center gap-2 text-xs text-zinc-300 hover:text-white hover:bg-zinc-900/60 transition-colors normal-case tracking-normal"
          >
            <Plus size={12} /> New watchlist…
          </button>
        )}
      </div>
    </>
  );
}
